import { useCallback, useEffect, useMemo, useRef } from "react"
import { useCatalogs, usePublishWebsites } from "../../api/queries"
import type { Catalog } from "../../api/types"
import { useStoredNumber } from "../../hooks/useStored"
import { StorageKey } from "../../lib/storage"

/**
 * Выбранный сайт и его каталог.
 *
 * Каталог сайта — тот, чьё имя совпадает с именем сайта: так его ищет бэк во
 * всех операциях публикации (`_catalogNavigator.Get().First(x => x.Name ==
 * website.Name)`). Выбор запоминается и общий для всех экранов: сайт, выбранный
 * на `/publish`, открыт и в карточке группы.
 */
export function usePublishTarget(onSiteChange?: (websiteId: number) => void) {
	const websites = usePublishWebsites()
	const catalogs = useCatalogs()

	const [websiteId, setWebsiteId] = useStoredNumber(StorageKey.publishSite, null)

	// колбэк держим в ref: иначе selectSite менялся бы каждый рендер и тянул за
	// собой эффект выбора сайта по умолчанию
	const notify = useRef(onSiteChange)
	notify.current = onSiteChange

	const sites = useMemo(() => websites.data ?? [], [websites.data])
	const site = sites.find(item => item.id === websiteId) ?? null

	const selectSite = useCallback(
		(next: number) => {
			setWebsiteId(next)
			notify.current?.(next)
		},
		[setWebsiteId]
	)

	// первый вход; сюда же попадает случай, когда запомненный сайт убрали из списка
	useEffect(() => {
		if (sites.length === 0) return
		if (websiteId !== null && sites.some(item => item.id === websiteId)) return
		selectSite(sites[0].id)
	}, [sites, websiteId, selectSite])

	const siteCatalog = useMemo<Catalog | null>(() => {
		if (!site || !catalogs.data) return null
		return catalogs.data.find(item => item.name === site.name) ?? null
	}, [site, catalogs.data])

	return {
		sites,
		site,
		siteCatalog,
		selectSite,
		isLoading: websites.isLoading || catalogs.isLoading,
	}
}
